import React, { useEffect } from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';

/* Easing curve used across the landing page for "luxurious" reveals.
   Cubic-bezier shape commonly called easeOutExpo — fast start, long settle. */
export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

/* When the page is loaded with `?capture=1` (the Playwright screenshot
   suite does this), force whileInView to fire once and never reverse —
   otherwise fullPage captures grab sections in their hidden initial
   state because they're out of the viewport at capture time. */
export const FREEZE_FOR_CAPTURE: boolean =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('capture');

/* Shared viewport config for whileInView animations across the landing
   page. Replay-by-default in production so each section's enter animation
   fires every time you scroll past it (paired with scroll-snap). */
export const replayingViewport = {
  once: FREEZE_FOR_CAPTURE,
  amount: 0.3,
  margin: '0px 0px -8% 0px',
} as const;

export const itemVariants: Variants = {
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.0, ease: EASE_OUT_EXPO },
  },
};

/* The hero has its own arrival — the mark's light, then its words, on
   one clock. See `landing/hero-intro.tsx`. */

interface MotionRevealProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  /** Default true — replays the reveal each time the element re-enters
   *  view, which pairs with section scroll-snap so each section's enter
   *  animation runs again on every visit. Pass false for one-shot reveals. */
  replay?: boolean;
}

/* Replacement for the old IntersectionObserver-based RevealOnScroll. Same
   ergonomics: wrap a block, get a fade-up reveal as it enters the viewport.
   Honors prefers-reduced-motion. */
export const MotionReveal: React.FC<MotionRevealProps> = ({
  children,
  delay,
  className,
  replay = true,
}) => {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{
        ...replayingViewport,
        once: replayingViewport.once || !replay,
      }}
      transition={{ duration: 1.1, ease: EASE_OUT_EXPO, delay: delay ?? 0 }}
    >
      {children}
    </motion.div>
  );
};

/* JS scroll-end section snap with directional hysteresis.

   Forward direction: the active section advances to N+1 once scrollY crosses
   `section[N].top + section[N].height * commitRatio`. The commit ratio is
   0.5 on small screens (<768px) and 0.25 on medium-and-up — so on a desktop,
   scrolling 25% into the current section commits the user to the next. A
   section taller than the screen (a product page's, on a phone) is read to
   its foot first: N+1 is committed to only once it has risen commitRatio of
   the way up the screen, rather than halfway through N's own content.

   Backward direction: the active section retreats to N-1 once scrollY drops
   below the current section's top (the boundary). Easier to retreat than
   to advance — by design, since backward scrolling is usually a deliberate
   "let me re-read" action.

   On scroll-end (~180ms after last scroll event), if a transition fired and
   the user is still in the "freshness zone" (≤ commitRatio past the section
   top), smooth-scroll to align the section. If they've scrolled deeper, leave
   them alone — they're committed to mid-section reading. Manual rAF easing
   (ease-out cubic, 600ms) for cross-browser consistency.

   Skips when:
   - reduced motion is preferred
   - the page is loaded with `?capture=1`
   - the user is within ~8px of the top or bottom of the document */
export const useSectionSnap = (
  sectionsRef: React.MutableRefObject<(HTMLElement | null)[]>,
) => {
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced || FREEZE_FOR_CAPTURE) return undefined;

    /* Tunables */
    const SCROLL_END_DEBOUNCE_MS = 180;
    const SMOOTH_DURATION_MS = 600;
    const ALIGNED_TOLERANCE_PX = 6;
    const EDGE_TOLERANCE_PX = 8;
    const MD_BREAKPOINT_PX = 768;

    /* Forward commit ratio. Smaller = less scroll needed before the active
       section flips to N+1 and a snap fires. The backward boundary stays at
       the section's top edge regardless. */
    const getCommitRatio = (): number =>
      window.innerWidth >= MD_BREAKPOINT_PX ? 0.25 : 0.5;

    let isProgrammatic = false;
    let endTimer: number | null = null;
    let lastIdx = 0;
    let lastY = window.scrollY;
    let needsSnap = false;

    const advanceForward = (y: number, commitRatio: number) => {
      const sections = sectionsRef.current;
      while (lastIdx + 1 < sections.length) {
        const el = sections[lastIdx];
        if (!el) break;
        const r = el.getBoundingClientRect();
        const top = r.top + y;
        // A section taller than the screen is read to its foot first: the
        // next one is committed to only once it has come commitRatio of
        // the way up the screen. For a section that fits, this is no later
        // than the plain threshold, so nothing changes for it.
        const threshold = Math.max(
          top + el.offsetHeight * commitRatio,
          top + el.offsetHeight - window.innerHeight * (1 - commitRatio),
        );
        if (y >= threshold) {
          lastIdx++;
          needsSnap = true;
        } else {
          break;
        }
      }
    };

    const retreatBackward = (y: number) => {
      while (lastIdx > 0) {
        const el = sectionsRef.current[lastIdx];
        if (!el) break;
        const r = el.getBoundingClientRect();
        const top = r.top + y;
        if (y < top) {
          lastIdx--;
          needsSnap = true;
        } else {
          break;
        }
      }
    };

    const containingIdx = (y: number): number => {
      const sections = sectionsRef.current;
      let idx = 0;
      for (let i = 0; i < sections.length; i++) {
        const el = sections[i];
        if (!el) continue;
        const top = el.getBoundingClientRect().top + y;
        if (y >= top - 1) idx = i;
      }
      return idx;
    };

    const smoothScrollTo = (targetY: number) => {
      const startY = window.scrollY;
      const distance = targetY - startY;
      const t0 = performance.now();
      isProgrammatic = true;

      const tick = (now: number) => {
        const t = Math.min(1, (now - t0) / SMOOTH_DURATION_MS);
        const eased = 1 - Math.pow(1 - t, 3);
        window.scrollTo(0, Math.round(startY + distance * eased));
        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          isProgrammatic = false;
        }
      };
      requestAnimationFrame(tick);
    };

    const performSnap = () => {
      if (!needsSnap || isProgrammatic) return;
      needsSnap = false;

      const y = window.scrollY;
      const viewportH = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      if (y < EDGE_TOLERANCE_PX) return;
      if (y + viewportH >= docH - EDGE_TOLERANCE_PX) return;

      const target = sectionsRef.current[lastIdx];
      if (!target) return;

      const r = target.getBoundingClientRect();
      const targetY = r.top + y;
      const distancePastTop = y - targetY;
      const sectionHeight = target.offsetHeight || viewportH;
      const commitRatio = getCommitRatio();

      if (Math.abs(distancePastTop) < ALIGNED_TOLERANCE_PX) return;

      // User scrolled deeper than the freshness window — leave them alone.
      if (distancePastTop > sectionHeight * commitRatio) return;

      smoothScrollTo(targetY);
    };

    const onScroll = () => {
      if (isProgrammatic) return;

      const y = window.scrollY;
      const commitRatio = getCommitRatio();
      if (y > lastY) advanceForward(y, commitRatio);
      else if (y < lastY) retreatBackward(y);
      lastY = y;

      if (endTimer !== null) clearTimeout(endTimer);
      endTimer = window.setTimeout(performSnap, SCROLL_END_DEBOUNCE_MS);
    };

    // Initialize from current scroll position so a refreshed mid-page page
    // doesn't fire a spurious snap on mount.
    lastIdx = containingIdx(window.scrollY);
    lastY = window.scrollY;

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (endTimer !== null) clearTimeout(endTimer);
    };
  }, [reduced, sectionsRef]);
};
